import React, { useMemo, useState } from "react";
import { apiGet, apiPost } from "../../services/api";
import { SupplierBillRow, SupplierOption } from "./types";
import { formatWhen } from "./utils";

interface PharmacyVendorBillsProps {
    pharmacySuppliers: SupplierOption[];
    financeDate: string;
    showToast: (args: { message: string; type: "success" | "error" | "info" | "warning" }) => void;
    onSuccess: () => Promise<void>;
}

export default function PharmacyVendorBills({ pharmacySuppliers, financeDate, showToast, onSuccess }: PharmacyVendorBillsProps) {
    const [selectedPharmacySupplierId, setSelectedPharmacySupplierId] = useState("");
    const [pharmacySupplierBills, setPharmacySupplierBills] = useState<SupplierBillRow[]>([]);
    const [loadingPharmacySupplierFinance, setLoadingPharmacySupplierFinance] = useState(false);
    const [selectedPharmacySupplierBillId, setSelectedPharmacySupplierBillId] = useState("");
    const [pharmacySupplierPayAmount, setPharmacySupplierPayAmount] = useState("");
    const [pharmacySupplierPayMode, setPharmacySupplierPayMode] = useState("Cash");
    const [pharmacySupplierReceipt, setPharmacySupplierReceipt] = useState("");
    const [payingPharmacySupplier, setPayingPharmacySupplier] = useState(false);

    const loadPharmacySupplierFinance = async (supplierId: string) => {
        if (!supplierId) {
            setPharmacySupplierBills([]);
            setSelectedPharmacySupplierBillId("");
            return;
        }
        setLoadingPharmacySupplierFinance(true);
        try {
            const bills = (await apiGet(`/api/pharmacy/supplier/${supplierId}/bills`)) as SupplierBillRow[];
            setPharmacySupplierBills(Array.isArray(bills) ? bills : []);
            if (Array.isArray(bills) && bills.length > 0) {
                setSelectedPharmacySupplierBillId(String(bills[0].id));
            }
        } catch {
            setPharmacySupplierBills([]);
        } finally {
            setLoadingPharmacySupplierFinance(false);
        }
    };

    const selectedPharmacySupplierBill = useMemo(
        () => pharmacySupplierBills.find((bill) => String(bill.id) === selectedPharmacySupplierBillId) || null,
        [pharmacySupplierBills, selectedPharmacySupplierBillId]
    );


    const handlePharmacySupplierPayment = async () => {
        const billId = Number(selectedPharmacySupplierBillId);
        const amount = Number(pharmacySupplierPayAmount || 0);
        if (!selectedPharmacySupplierId) { showToast({ message: "Select pharmacy supplier.", type: "warning" }); return; }
        if (!billId) { showToast({ message: "Select pharmacy bill.", type: "warning" }); return; }
        if (!Number.isFinite(amount) || amount <= 0) { showToast({ message: "Enter valid payment amount.", type: "warning" }); return; }

        const balance = Number(selectedPharmacySupplierBill?.balance || 0);
        if (balance > 0 && amount > balance) { showToast({ message: "Amount exceeds bill balance.", type: "warning" }); return; }

        setPayingPharmacySupplier(true);
        try {
            await apiPost("/api/pharmacy/supplier-payment", {
                purchase_bill_id: billId,
                amount,
                mode: pharmacySupplierPayMode || "Cash",
                receipt_number: pharmacySupplierReceipt.trim() || null,
                payment_date: financeDate,
            });

            showToast({ message: "Pharmacy supplier payment saved.", type: "success" });
            setPharmacySupplierReceipt("");
            setPharmacySupplierPayAmount("");
            await loadPharmacySupplierFinance(selectedPharmacySupplierId);
            await onSuccess();
        } catch {
            showToast({ message: "Failed to save pharmacy supplier payment.", type: "error" });
        } finally {
            setPayingPharmacySupplier(false);
        }
    };

    const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem', background: '#f8fafc' };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: 'var(--mod-text-main)' }}>Pharmacy Vendor Bills</h3>
                <select style={{ ...inputStyle, marginBottom: '16px' }} value={selectedPharmacySupplierId} onChange={(e) => { setSelectedPharmacySupplierId(e.target.value); void loadPharmacySupplierFinance(e.target.value); }}>
                    <option value="">Select supplier</option>
                    {pharmacySuppliers.map((s) => <option key={s.id} value={s.id}>{s.name} {s.balance_due ? `(₹${s.balance_due})` : ""}</option>)}
                </select>

                {loadingPharmacySupplierFinance && <div style={{ color: 'var(--mod-text-muted)', fontSize: '0.85rem' }}>Loading bills...</div>}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {pharmacySupplierBills.filter(b => Number(b.balance || 0) > 0).map((b) => (
                        <div key={b.id} onClick={() => setSelectedPharmacySupplierBillId(String(b.id))} style={{ padding: '12px', borderRadius: '8px', border: selectedPharmacySupplierBillId === String(b.id) ? '1px solid #fed7aa' : '1px solid #e2e8f0', background: selectedPharmacySupplierBillId === String(b.id) ? '#fff7ed' : '#fff', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Inv: {b.invoice_no || `ID:${b.id}`}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--mod-text-muted)' }}>{formatWhen(b.date)}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ color: '#dc2626', fontWeight: 700, fontSize: '0.95rem' }}>₹{Number(b.balance || 0).toLocaleString("en-IN")}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--mod-text-muted)' }}>Total: ₹{Number(b.total || 0).toLocaleString("en-IN")}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: 'var(--mod-text-main)' }}>Process Payment</h3>
                {selectedPharmacySupplierBill ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Amount (₹)</label>
                                <input style={inputStyle} type="number" placeholder={`Max ₹${selectedPharmacySupplierBill.balance}`} value={pharmacySupplierPayAmount} onChange={(e) => setPharmacySupplierPayAmount(e.target.value)} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Mode</label>
                                <select style={inputStyle} value={pharmacySupplierPayMode} onChange={(e) => setPharmacySupplierPayMode(e.target.value)}>
                                    <option value="Cash">Cash</option><option value="UPI">UPI</option><option value="Bank">Bank</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Receipt / UTR (Optional)</label>
                            <input style={inputStyle} value={pharmacySupplierReceipt} onChange={(e) => setPharmacySupplierReceipt(e.target.value)} placeholder="e.g. TXN12345" />
                        </div>
                        <button style={{ padding: '12px', borderRadius: '8px', background: 'linear-gradient(135deg, var(--mod-amber), #ea580c)', color: '#fff', border: 'none', fontWeight: 700, cursor: (payingPharmacySupplier || !pharmacySupplierPayAmount) ? 'not-allowed' : 'pointer', opacity: (payingPharmacySupplier || !pharmacySupplierPayAmount) ? 0.6 : 1 }} onClick={() => void handlePharmacySupplierPayment()}>
                            {payingPharmacySupplier ? "Processing..." : `Pay Bill #${selectedPharmacySupplierBill.invoice_no || selectedPharmacySupplierBill.id}`}
                        </button>
                    </div>
                ) : (
                    <div className="mod-empty-state" style={{ height: '200px', border: 'none', background: '#f8fafc' }}>
                        <span style={{ fontSize: '1.5rem' }}>🧾</span>
                        <p style={{ marginTop: '8px', fontSize: '0.9rem' }}>Select a pending bill to initiate payment.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
